import * as React from 'react';
import { ProgressSteps, NumberedStep } from 'baseui/progress-steps';
import { StatefulTooltip } from 'baseui/tooltip';
import yaml from 'js-yaml';
import { useStyletron, DarkTheme, ThemeProvider } from 'baseui';
import usePipelineSteps from '../../hooks/useTopoSort';

function ComplexContent({ detail }) {
  const [css, theme] = useStyletron();
  return (
    <ThemeProvider theme={DarkTheme}>
      <div
        className={css({
          padding: theme.sizing.scale600,
          backgroundColor: theme.colors.backgroundSecondary,
          color: theme.colors.contentPrimary,
          borderRadius: theme.borders.radius200,
          boxShadow: theme.lighting.shadow400,
          maxWidth: '360px',
          fontSize: '14px',
        })}
      >
        <h4
          className={css({
            margin: 0,
            marginBottom: theme.sizing.scale300,
            fontSize: '16px',
          })}
        >
          Step Details
        </h4>
        <pre
          className={css({
            margin: 0,
            whiteSpace: 'pre-wrap',
            fontSize: '12px',
            backgroundColor: theme.colors.backgroundTertiary,
            padding: theme.sizing.scale300,
            borderRadius: theme.borders.radius100,
            overflowX: 'auto',
          })}
        >
          {detail}
        </pre>
      </div>
    </ThemeProvider>
  );
}

export default function PipelineProgressStepComponent({
  yamlString,
  finished,
  completedSteps = [], // array of task names
}) {
  const [css, theme] = useStyletron();
  const [current, setCurrent] = React.useState(0);
  const { stepKeys, filters } = usePipelineSteps(yamlString);



  // 2️⃣ Derive completed-set and active step
  const finishedSet = React.useMemo(() => new Set(completedSteps), [completedSteps]);


  /* 2️⃣ build finished-set & decide current pointer */
  React.useEffect(() => {
    if (!stepKeys.length) return;

    // when `finished` is true mark every step as done
    const finishedSet = finished
      ? new Set(stepKeys)
      : new Set(completedSteps);

    const firstIncomplete = stepKeys.findIndex(k => !finishedSet.has(k));
    setCurrent(firstIncomplete === -1 ? stepKeys.length - 1 : firstIncomplete);
  }, [finished, completedSteps, stepKeys]);

  if (!stepKeys.length) 
    return <div>No steps defined in the provided YAML.</div>;

  return (
    <ProgressSteps current={current}>
      {stepKeys.map((key, idx) => {
        const detail = filters[key] || {};
        const content = JSON.stringify(detail, null, 2);

        return (
          <NumberedStep
            key={key}
            isCompleted={finishedSet.has(key) || finished}
            title={
              <StatefulTooltip
                placement="bottom"
                content={() => <ComplexContent detail={content} />}
              >
                <span style={{ cursor: 'pointer'}}>{key}</span>
              </StatefulTooltip>
            }
    
            onClick={() => setCurrent(idx)}
          />
        );
      })}
    </ProgressSteps>
  );
}